import React from 'react'
import gql from 'graphql-tag'
import { defaultDataIdFromObject } from 'apollo-cache-inmemory'
import { Query, Mutation } from 'react-apollo'
import { PureQueryOptions } from 'apollo-client'
import {
  LibraryAgentLanguage,
  LibraryAgentLanguageValue
} from '../library/library-agent-language'
import { ContrastFormattedMessage } from '../../../../components/contrast-formatted-message'
import { TagModal } from 'contrastBase/Modals'
import { excludeFrom } from 'contrastBase/Tags'
import { fromTags, toTags } from '../../helpers/helpers'

export const availableTagsQuery = gql`
  query AvailableLibraryTags($organizationID: String!) {
    organizationLibraryTags(organizationID: $organizationID) {
      tags
    }
  }
`
interface Library {
  hash: string
  language: LibraryAgentLanguage
  tags: string[]
}

interface ModifyTagProps {
  add: string[]
  remove: string[]
}

interface ModalProps {
  isOpen: boolean
  closeModal: () => void
  initialTags: string[]
  organizationID: string
}

interface MutationProps {
  refresh?: () => Promise<any> | (() => void)
  refetchQueries?: PureQueryOptions[]
}

function ModifyLibrariesTags({
  isOpen,
  closeModal,
  initialTags,
  organizationID,
  modifyTags,
  multipleLibraries
}: ModalProps & {
  multipleLibraries: boolean
  modifyTags: (props: ModifyTagProps) => Promise<any>
}) {
  return (
    <Query query={availableTagsQuery} variables={{ organizationID }}>
      {({ data, loading, refetch, ...props }) => {
        if (props.error) {
          console.error(props.error)
          throw new Error('Error caught when modifying library tags')
        }
        const availableTags = loading ? [] : data.organizationLibraryTags.tags
        return (
          <ContrastFormattedMessage
            id={
              multipleLibraries
                ? 'title_libraries_tagLibraries'
                : 'title_libraries_tagLibrary'
            }
          >
            {(title) => (
              <TagModal
                initialAvailableTags={toTags(availableTags)}
                initialSelectedTags={toTags(initialTags)}
                onConfirm={(selectedTags) => {
                  const tags = fromTags(selectedTags)
                  const add = excludeFrom(tags, initialTags)
                  const remove = excludeFrom(initialTags, tags)

                  if (add.length + remove.length > 0) {
                    // eslint-disable-next-line @typescript-eslint/no-floating-promises
                    modifyTags({ add, remove }).then(refetch)
                  }
                  closeModal()
                }}
                onCancel={closeModal}
                title={title}
                isOpen={isOpen}
              />
            )}
          </ContrastFormattedMessage>
        )
      }}
    </Query>
  )
}

const libraryTagsFragment = gql`
  fragment library on Library {
    tags
  }
`

function isLibraryAgentLanguage(
  value: string | LibraryAgentLanguage
): value is LibraryAgentLanguage {
  return (value as LibraryAgentLanguage).getValue !== undefined
}

/**
 * Produces a library ID object appropriate for the Apollo cache.
 *
 * The library.language property was originally typed as LibraryAgentLanguage
 * but was discovered to be a string (LibraryAgentLanguageValue) when TS-5521
 * was opened. This function will now accept either the string or the LibraryAgentLanguage value.
 */
export const buildApolloCacheId = ({
  hash,
  language
}:
  | Library
  | {
      hash: Library['hash']
      language: LibraryAgentLanguageValue | LibraryAgentLanguage
    }) => {
  const languageValue = isLibraryAgentLanguage(language)
    ? language.getValue()
    : language
  return (
    defaultDataIdFromObject({
      __typename: 'Library',
      id: hash + '|' + languageValue
    }) || ''
  )
}

export const updateTagsForCache = ({
  existing,
  toAdd,
  toRemove
}: {
  existing: string[]
  toAdd: string[]
  toRemove: string[]
}) => {
  return existing.filter((t) => toRemove.every((r) => r !== t)).concat(toAdd)
}

function updateTags(libraries: Library[], toAdd: string[], toRemove: string[]) {
  return (proxy) => {
    libraries.forEach((lib) => {
      const id = buildApolloCacheId(lib)

      const { tags: existing }: any = proxy.readFragment({
        id,
        fragment: libraryTagsFragment
      })

      proxy.writeFragment({
        id,
        fragment: libraryTagsFragment,
        data: {
          tags: updateTagsForCache({
            existing,
            toAdd,
            toRemove
          }),
          __typename: 'Library'
        }
      })
    })
  }
}

function getOptimisticResponse(fieldName) {
  return {
    [fieldName]: {
      _placeholder: true,
      __typename: 'LibraryBulkResponse'
    },
    __typename: 'Mutation'
  }
}

class ModifyTagsMutation extends Mutation<
  any,
  ModifyTagProps & {
    organizationID: string
    ids: string[]
  }
> {}

const modifyLibrariesTagsMutationName = 'modifyLibraryTags'
const modifyLibrariesTagsMutation = gql`
  mutation ${modifyLibrariesTagsMutationName}(
    $organizationID: ID!
    $add: [ID!]
    $remove: [ID!]
    $ids: [ID!]!
  ) {
    ${modifyLibrariesTagsMutationName}(
      input: {
        organizationID: $organizationID
        add: $add
        remove: $remove
        ids: $ids
      }
    ) {
      _placeholder
    }
  }
`

export function ModifyLibrariesTagsModal({
  organizationID,
  libraries,
  refetchQueries,
  refresh,
  initialTags,
  closeModal,
  isOpen
}: ModalProps &
  MutationProps & {
    libraries: Library[]
  }) {
  return (
    <ModifyTagsMutation mutation={modifyLibrariesTagsMutation} ignoreResults>
      {(mutate) => {
        return (
          <ModifyLibrariesTags
            isOpen={isOpen}
            initialTags={initialTags}
            closeModal={closeModal}
            multipleLibraries={libraries.length > 1}
            organizationID={organizationID}
            modifyTags={({ add, remove }) => {
              return mutate({
                refetchQueries,
                variables: {
                  add,
                  remove,
                  organizationID,
                  ids: libraries.map((lib) => lib.hash)
                },
                optimisticResponse: getOptimisticResponse(
                  modifyLibrariesTagsMutationName
                ),
                update: updateTags(libraries, add, remove)
              }).then(refresh)
            }}
          />
        )
      }}
    </ModifyTagsMutation>
  )
}

class ModifyTagsByFilterMutation extends Mutation<
  any,
  ModifyTagProps & {
    organizationID: string
    excludedHashes: string[]
    filters: any
  }
> {}

const modifyLibrariesTagsByFilterMutationName = 'modifyLibrariesTagsByFilters'
const modifyLibrariesTagsByFilterMutation = gql`
  mutation ${modifyLibrariesTagsByFilterMutationName}(
    $organizationID: ID!
    $add: [String!]!
    $remove: [String!]!
    $filters: LibraryFilterArg!
    $excludedHashes: [String!]!
  ) {
    ${modifyLibrariesTagsByFilterMutationName}(
      input: {
        organizationID: $organizationID
        add: $add
        remove: $remove
        filters: $filters
        excludedHashes: $excludedHashes
      }
    ) {
      _placeholder
    }
  }
`

export function ModifyLibrariesTagsByFilterModal({
  filters: { filterText, quickFilter, aggregations },
  additionalFilters,
  organizationID,
  excludedHashes,
  refresh,
  refetchQueries,
  initialTags,
  closeModal,
  isOpen,
  libraries
}: ModalProps &
  MutationProps & {
    organizationID: string
    libraries: Library[]
    excludedHashes: string[]
    filters: {
      filterText: string
      quickFilter: string
      aggregations: Record<string, string[]>
    }
    additionalFilters?: { [key: string]: any }
  }) {
  return (
    <ModifyTagsByFilterMutation mutation={modifyLibrariesTagsByFilterMutation}>
      {(mutate) => {
        return (
          <ModifyLibrariesTags
            initialTags={initialTags}
            closeModal={closeModal}
            isOpen={isOpen}
            multipleLibraries={true}
            organizationID={organizationID}
            modifyTags={({ add, remove }) =>
              mutate({
                refetchQueries,
                variables: {
                  add,
                  remove,
                  filters: Object.assign({}, aggregations, {
                    filterText,
                    quickFilter,
                    ...additionalFilters
                  }),
                  organizationID,
                  excludedHashes
                },
                optimisticResponse: getOptimisticResponse(
                  modifyLibrariesTagsByFilterMutationName
                ),
                update: updateTags(libraries, add, remove)
              }).then(refresh)
            }
          />
        )
      }}
    </ModifyTagsByFilterMutation>
  )
}
